import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress
} from '@mui/material';
import { CiTrash } from 'react-icons/ci';
import { useTeam } from '../../../../shared/context/teamContext';
import axiosInstance from '../../../../shared/services/api/axiosInstance';

// Cryptique Design System
const styles = {
  primarycolor: "#0f172a",
  primaryColor: "#0f172a",
  backgroundColor: "#ffffff",
  textPrimary: "#0f172a",
  textSecondary: "#666666",
  successColor: "#0f766e",
  warningColor: "#b45309",
  errorColor: "#b91c1c",
  headingFont: { fontFamily: "'Raleway', sans-serif", fontWeight: 600 },
  bodyFont: { fontFamily: "'Raleway', sans-serif", fontWeight: 400 }
};

const MembersPage = () => {
  const { selectedTeam } = useTeam();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('user');
  const [members, setMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [currentUserEmail, setCurrentUserEmail] = useState('');
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [updatingRoles, setUpdatingRoles] = useState(new Set());
  const [deletingMembers, setDeletingMembers] = useState(new Set());

  // Get current user email
  useEffect(() => {
    const getUserEmail = () => {
      try {
        const userString = localStorage.getItem('User');
        if (userString) {
          const userObj = JSON.parse(userString);
          setCurrentUserEmail(userObj.email || '');
        }
      } catch (err) {
        console.error('Error getting user email:', err);
      }
    };
    getUserEmail();
  }, []);

  // Fetch members and determine current user role
  useEffect(() => {
    if (selectedTeam?.id || selectedTeam?._id) {
      fetchMembers();
      fetchPendingInvites();
    }
  }, [selectedTeam, currentUserEmail]);

  const fetchMembers = async () => {
    if (!selectedTeam?.id && !selectedTeam?._id) return;
    
    try {
      setLoading(true);
      const teamId = selectedTeam.id || selectedTeam._id;
      const response = await axiosInstance.get(`/team/${teamId}/members`);
      
      const membersList = response.data?.members || response.data || [];
      setMembers(membersList);
      
      // Determine current user's role
      // Owner is the first member (index 0), others are admin or user
      const currentMember = membersList.find((member, index) => 
        member.email?.toLowerCase() === currentUserEmail.toLowerCase() ||
        member.userId?.toString() === localStorage.getItem('userId')
      );
      
      if (currentMember) {
        const memberIndex = membersList.findIndex(m => 
          m.email === currentMember.email || m.userId === currentMember.userId
        );
        // First member (index 0) is owner, otherwise use their role
        if (memberIndex === 0) {
          setCurrentUserRole('owner');
        } else {
          setCurrentUserRole(currentMember.role || 'user');
        }
      }
    } catch (err) {
      console.error('Error fetching members:', err);
      setError(err.response?.data?.message || 'Failed to fetch members');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingInvites = async () => {
    if (!selectedTeam?.id && !selectedTeam?._id) return;
    
    try {
      const teamId = selectedTeam.id || selectedTeam._id;
      const response = await axiosInstance.get(`/team/${teamId}/pending-invites`);
      setPendingInvites(response.data.pendingInvites || []);
    } catch (err) {
      console.error('Error fetching pending invites:', err);
    }
  };

  const handleInvite = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    if (!selectedTeam?.id && !selectedTeam?._id) {
      setError('No team selected');
      return;
    }

    // Check if user is admin
    if (currentUserRole !== 'admin' && currentUserRole !== 'owner') {
      setError('Only admins can invite members');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      const teamId = selectedTeam.id || selectedTeam._id;
      const response = await axiosInstance.post('/team/invite', {
        email: email.trim(),
        role: role,
        teamId: teamId
      });

      if (response.data.addedImmediately) {
        setSuccess(`Successfully added ${response.data.user.email} to the team.`);
      } else {
        setSuccess(`Invitation sent to ${email.trim()}. They will be added when they sign up.`);
      }

      setEmail('');
      await fetchMembers();
      await fetchPendingInvites();
    } catch (err) {
      console.error('Error inviting member:', err);
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to invite member');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (memberEmail, newRole) => {
    if (!selectedTeam?.id && !selectedTeam?._id) return;
    
    // Check if user is admin
    if (currentUserRole !== 'admin' && currentUserRole !== 'owner') {
      setError('Only admins can change roles');
      return;
    }

    // Don't allow changing owner role
    const member = members.find(m => m.email === memberEmail);
    if (member?.role === 'owner') {
      setError('Cannot change owner role');
      return;
    }

    try {
      setUpdatingRoles(prev => new Set(prev).add(memberEmail));
      setError(null);
      setSuccess(null);
      
      const teamId = selectedTeam.id || selectedTeam._id;
      console.log('Updating role:', { teamId, email: memberEmail, newRole });
      
      const response = await axiosInstance.post('/team/update-member-role', {
        teamId: teamId,
        email: memberEmail,
        newRole: newRole
      });

      console.log('Role update response:', response.data);
      
      // Refresh members list
      await fetchMembers();
      setSuccess('Role updated successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error updating role:', err);
      console.error('Error response:', err.response?.data);
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to update role');
    } finally {
      setUpdatingRoles(prev => {
        const newSet = new Set(prev);
        newSet.delete(memberEmail);
        return newSet;
      });
    }
  };

  const handleDeleteMember = async (memberEmail) => {
    if (!selectedTeam?.id && !selectedTeam?._id) return;
    
    // Check if user is admin
    if (currentUserRole !== 'admin' && currentUserRole !== 'owner') {
      setError('Only admins can remove members');
      return;
    }

    // Don't allow removing owner
    const member = members.find(m => m.email === memberEmail);
    if (member && isOwner(member)) {
      setError('Cannot remove owner');
      return;
    }

    if (!window.confirm(`Are you sure you want to remove ${memberEmail} from the team?`)) {
      return;
    }

    try {
      setDeletingMembers(prev => new Set(prev).add(memberEmail));
      setError(null);
      
      const teamId = selectedTeam.id || selectedTeam._id;
      await axiosInstance.post('/team/remove-member', {
        teamId: teamId,
        email: memberEmail
      });

      await fetchMembers();
      setSuccess('Member removed successfully');
    } catch (err) {
      console.error('Error removing member:', err);
      setError(err.response?.data?.message || 'Failed to remove member');
    } finally {
      setDeletingMembers(prev => {
        const newSet = new Set(prev);
        newSet.delete(memberEmail);
        return newSet;
      });
    }
  };

  const handleCancelInvite = async (inviteEmail) => {
    if (!selectedTeam?.id && !selectedTeam?._id) return;
    
    // Check if user is admin
    if (currentUserRole !== 'admin' && currentUserRole !== 'owner') {
      setError('Only admins can cancel invitations');
      return;
    }

    if (!window.confirm(`Cancel invitation for ${inviteEmail}?`)) {
      return;
    }

    try {
      setError(null);
      const teamId = selectedTeam.id || selectedTeam._id;
      await axiosInstance.post('/team/cancel-invite', {
        teamId: teamId,
        email: inviteEmail
      });

      await fetchPendingInvites();
      setSuccess('Invitation cancelled successfully');
    } catch (err) {
      console.error('Error cancelling invite:', err);
      setError(err.response?.data?.message || 'Failed to cancel invitation');
    }
  };

  // Get first letter for avatar
  const getInitial = (email) => {
    if (!email) return '?';
    return email.charAt(0).toUpperCase();
  };

  // Check if user is admin
  const isAdmin = currentUserRole === 'admin' || currentUserRole === 'owner';

  // Check if member is owner (first member in the list)
  const isOwner = (member) => {
    const memberIndex = members.findIndex(m => 
      m.email === member.email || m.userId === member.userId
    );
    return memberIndex === 0;
  };

  // Check if member is current user
  const isCurrentUser = (member) => 
    member.email?.toLowerCase() === currentUserEmail.toLowerCase();

  return (
    <Box sx={{ width: '100%', py: 2 }}>
      {/* Invite Member Section */}
      <Box
        sx={{
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          p: 2.5,
          mb: 3,
          backgroundColor: styles.backgroundColor
        }}
      >
        <Typography
          variant="h6"
          sx={{
            ...styles.headingFont,
            color: styles.textPrimary,
            fontSize: '1.1rem',
            mb: 2
          }}
        >
          Invite member
        </Typography>

        <Box
          component="form"
          onSubmit={handleInvite}
          sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 1.5 }}
        >
          <TextField
            fullWidth
            placeholder="email@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleInvite(e);
              }
            }}
            disabled={!isAdmin || loading}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '6px',
                fontSize: '0.9375rem',
                '& fieldset': {
                  borderColor: '#E5E7EB'
                },
                '&:hover fieldset': {
                  borderColor: styles.primaryColor
                },
                '&.Mui-focused fieldset': {
                  borderColor: styles.primaryColor
                }
              }
            }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              disabled={!isAdmin || loading}
              sx={{
                borderRadius: '6px',
                fontSize: '0.9375rem',
                backgroundColor: styles.backgroundColor,
                color: styles.textPrimary,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#E5E7EB',
                  borderWidth: '1px'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: styles.primaryColor
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: styles.primaryColor,
                  borderWidth: '1px'
                },
                '& .MuiSelect-icon': {
                  color: styles.textPrimary
                },
                '&.Mui-disabled': {
                  backgroundColor: '#F9FAFB',
                  color: styles.textSecondary
                }
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: '6px',
                    border: '1px solid #E5E7EB',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    mt: 0.5,
                    '& .MuiMenuItem-root': {
                      fontSize: '0.9375rem',
                      color: styles.textPrimary,
                      '&:hover': {
                        backgroundColor: '#F9FAFB'
                      },
                      '&.Mui-selected': {
                        backgroundColor: '#F9FAFB',
                        color: styles.primaryColor,
                        '&:hover': {
                          backgroundColor: '#F3F4F6'
                        }
                      }
                    }
                  }
                }
              }}
            >
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Typography
          variant="body2"
          sx={{
            ...styles.bodyFont,
            color: styles.textSecondary,
            fontSize: '0.875rem',
            mb: error || success ? 1.5 : 0
          }}
        >
          ⓘ Enter an email and press 'Enter' to invite a member and assign{' '}
          <span style={{ color: styles.primaryColor, cursor: 'pointer' }}>roles</span>.
        </Typography>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mt: 1.5, fontSize: '0.875rem', py: 1 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mt: 1.5, fontSize: '0.875rem', py: 1 }}>
            {success}
          </Alert>
        )}
      </Box>

      {/* Members List */}
      <Box>
        {loading && members.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} sx={{ color: styles.primaryColor }} />
          </Box>
        ) : (
          <Box
            sx={{
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              overflow: 'hidden',
              backgroundColor: styles.backgroundColor
            }}
          >
            {/* Active Members */}
            {members.map((member, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 2,
                  px: 2.5,
                  borderBottom: index < members.length - 1 || pendingInvites.length > 0 ? '1px solid #E5E7EB' : 'none',
                  '&:hover': {
                    backgroundColor: '#F9FAFB'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                  {/* Avatar */}
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      background: '#4b57aa',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.9375rem'
                    }}
                  >
                    {getInitial(member.email)}
                  </Box>

                  {/* Email and Tags */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography
                      sx={{
                        ...styles.bodyFont,
                        color: styles.textPrimary,
                        fontSize: '0.9375rem'
                      }}
                    >
                      {member.email}
                    </Typography>
                    {isOwner(member) && (
                      <Chip
                        label="Owner"
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.75rem',
                          background: '#4b57aa',
                          color: 'white',
                          fontWeight: 500,
                          borderRadius: '4px',
                          '& .MuiChip-label': {
                            px: 1
                          }
                        }}
                      />
                    )}
                    {isCurrentUser(member) && (
                      <Chip
                        label="You"
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.75rem',
                          background: '#4b57aa',
                          color: 'white',
                          fontWeight: 500,
                          borderRadius: '4px',
                          '& .MuiChip-label': {
                            px: 1
                          }
                        }}
                      />
                    )}
                  </Box>
                </Box>

                {/* Role Dropdown */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <Select
                      value={member.role || 'user'}
                      onChange={(e) => handleRoleChange(member.email, e.target.value)}
                      disabled={!isAdmin || isOwner(member) || updatingRoles.has(member.email)}
                      sx={{
                        borderRadius: '6px',
                        fontSize: '0.9375rem',
                        height: '36px',
                        backgroundColor: styles.backgroundColor,
                        color: styles.textPrimary,
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#E5E7EB',
                          borderWidth: '1px'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: styles.primaryColor
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: styles.primaryColor,
                          borderWidth: '1px'
                        },
                        '& .MuiSelect-icon': {
                          color: styles.textPrimary
                        },
                        '&.Mui-disabled': {
                          backgroundColor: '#F9FAFB',
                          color: styles.textSecondary,
                          '& .MuiSelect-icon': {
                            color: styles.textSecondary
                          }
                        }
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            borderRadius: '6px',
                            border: '1px solid #E5E7EB',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                            mt: 0.5,
                            '& .MuiMenuItem-root': {
                              fontSize: '0.9375rem',
                              color: styles.textPrimary,
                              '&:hover': {
                                backgroundColor: '#F9FAFB'
                              },
                              '&.Mui-selected': {
                                backgroundColor: '#F9FAFB',
                                color: styles.primaryColor,
                                '&:hover': {
                                  backgroundColor: '#F3F4F6'
                                }
                              }
                            }
                          }
                        }
                      }}
                    >
                      <MenuItem value="admin">Admin</MenuItem>
                      <MenuItem value="user">User</MenuItem>
                    </Select>
                  </FormControl>

                  {/* Delete Button */}
                  {isAdmin && !isOwner(member) && (
                    <Tooltip title="Remove member">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteMember(member.email)}
                        disabled={deletingMembers.has(member.email)}
                        sx={{
                          color: styles.errorColor,
                          padding: '4px',
                          '&:hover': {
                            backgroundColor: '#FEE2E2'
                          }
                        }}
                      >
                        {deletingMembers.has(member.email) ? (
                          <CircularProgress size={14} />
                        ) : (
                          <CiTrash size={16} />
                        )}
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            ))}

            {/* Pending Invites */}
            {pendingInvites.map((invite, index) => (
              <Box
                key={`invite-${index}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 2,
                  px: 2.5,
                  borderBottom: index < pendingInvites.length - 1 ? '1px solid #E5E7EB' : 'none',
                  backgroundColor: '#FFFBEB',
                  '&:hover': {
                    backgroundColor: '#FEF3C7'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                  {/* Avatar */}
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      background: '#4b57aa',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.9375rem'
                    }}
                  >
                    {getInitial(invite.email)}
                  </Box>

                  {/* Email and Pending tag */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      sx={{
                        ...styles.bodyFont,
                        color: styles.textPrimary,
                        fontSize: '0.9375rem'
                      }}
                    >
                      {invite.email}
                    </Typography>
                    <Chip
                      label="Pending"
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.75rem',
                        background: '#4b57aa',
                        color: 'white',
                        fontWeight: 500,
                        borderRadius: '4px',
                        '& .MuiChip-label': {
                          px: 1
                        }
                      }}
                    />
                  </Box>
                </Box>

                {/* Role and Cancel */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={invite.role === 'admin' ? 'Admin' : 'User'}
                    size="small"
                    sx={{
                      height: 24,
                      fontSize: '0.75rem',
                      background: '#4b57aa',
                      color: 'white',
                      fontWeight: 500,
                      borderRadius: '4px'
                    }}
                  />
                  {isAdmin && (
                    <Tooltip title="Cancel invitation">
                      <IconButton
                        size="small"
                        onClick={() => handleCancelInvite(invite.email)}
                        sx={{
                          color: styles.errorColor,
                          padding: '4px',
                          '&:hover': {
                            backgroundColor: '#FEE2E2'
                          }
                        }}
                      >
                        <CiTrash size={16} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            ))}

            {members.length === 0 && pendingInvites.length === 0 && (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography
                  sx={{
                    ...styles.bodyFont,
                    color: styles.textSecondary,
                    fontSize: '0.9375rem'
                  }}
                >
                  No members yet
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MembersPage;
